<script>
import {defineComponent} from 'vue';
import Cookies from 'js-cookie';

const REQUIRED_EXP_KEY = 'ccnt_required_expiration';
const WEEK_IN_SEC = 3600*24*7;
const MONTH_IN_SEC = 3600*24*31;
const HTML_ID = 'cookie-consent-settings';

export default defineComponent({
    name: 'CookieConsentApp',

    props: {
        state_cookie: {
            type: Object,
            default: () => ({}),
        },
        reminder_expiration: { type: Number, default: WEEK_IN_SEC },
        cookie_types: {
            type: Array,
            default: () => [],
        },
        strings: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            isVisible: false,
            settingsVisible: false,
            initialCookieTypes: [],
            acceptedCookieTypes: [],
            HTML_ID,
        };
    },

    watch: {
        isVisible(val) {
            if (val) {
                document.body.classList.add('ccnt--visible');
            } else {
                document.body.classList.remove('ccnt--visible');
            }
        },
        settingsVisible(val) {
            if (val) {
                document.body.classList.add('ccnt--settingsVisible');
            } else {
                document.body.classList.remove('ccnt--settingsVisible');
            }
        },
    },

    created() {
        const data = JSON.parse(atob(Cookies.get(this.state_cookie.key) || '') || '{}');
        this.acceptedCookieTypes = this.cookie_types.filter(t => {
            return t.required || (data.act || []).includes(t.id);
        })
        this.initialCookieTypes = this.acceptedCookieTypes.slice();
        this.onHashChange();
        window.addEventListener('hashchange', this.onHashChange);
    },

    mounted() {
        if (!this.userChoiceExists() && window.location.hostname !== 'localhost') {
            this.open();
        }
        window.addEventListener('resize', this.onWindowResize);
        window.addEventListener('orientationchange', this.onWindowResize);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('orientationchange', this.onWindowResize);
        window.removeEventListener('hashchange', this.onHashChange);
    },

    methods: {

        toggle() {
            this.isVisible ? this.close() : this.open();
        },

        open() {
            this.isVisible = true;
        },

        close() {
            this.isVisible = false;
            if (window.location.hash === `#${HTML_ID}`) {
                history.replaceState({}, '', location.href.replace(`#${HTML_ID}`, ''));
            }

            // Reload page when active cookie choices change
            // Compare initial with currently selected cookie types
            if (
                this.initialCookieTypes.sort(ctsort).map(ctgetid).join('') !==
                this.acceptedCookieTypes.sort(ctsort).map(ctgetid).join('')
            ) {
                window.location.href = window.location.href;
            }
        },

        onHashChange() {
            if (window.location.hash === `#${HTML_ID}`) {
                this.open();
                this.$nextTick(() => {
                    this.settingsVisible = true;
                })
            }
        },

        onWindowResize() {
            if (this.settingsVisible && this.$refs.settingsBody) {
                this.onSettingsEnter(this.$refs.settingsBody);
            }
        },

        acceptAllCookies() {
            this.acceptedCookieTypes = this.cookie_types;
            this.saveSettings();
        },

        acceptOnlyRequiredCookies() {
            this.acceptedCookieTypes = this.cookie_types.filter(ct => ct.required);
            this.saveSettings();
        },

        saveSettings() {
            let onlyRequired = true
            this.acceptedCookieTypes.forEach(ct => {
                if (!ct.required) {
                    onlyRequired = false;
                }
            });
            if (onlyRequired) {
                Cookies.remove(this.state_cookie.key, this.getCookieOptions());
                const now = new Date();
                localStorage.setItem(REQUIRED_EXP_KEY, (new Date(now.getTime() + this.reminder_expiration*1000)).getTime().toString());
            } else {
                const payload = btoa(JSON.stringify({
                    act: this.acceptedCookieTypes.filter(ct => !ct.required).map(ct => ct.id),
                }));
                Cookies.set(this.state_cookie.key, payload, this.getCookieOptions());
                localStorage.removeItem(REQUIRED_EXP_KEY);
            }
            this.settingsVisible = false;
            this.close();
        },

        getCookieOptions() {
            return {
                domain: this.state_cookie.domain || '.' + window.location.host.split('.').reverse().slice(0, 2).reverse().join('.'),
                expires: new Date((new Date()).getTime() + (this.state_cookie.expiration || MONTH_IN_SEC)*1000),
                path: this.state_cookie.path || '/',
            };
        },

        userChoiceExists() {
            if (Cookies.get(this.state_cookie.key)) {
                return true;
            }
            const reqExp = parseInt(localStorage.getItem(REQUIRED_EXP_KEY));
            return new Date(reqExp || 0) > new Date();
        },

        str(...pathFragments) {
            let o = this.strings;
            let backup = [];
            for (let f of pathFragments) {
                if (typeof o[f] === 'string') {
                    return o[f];
                } else if (o[f] && typeof o[f] === 'object') {
                    o = o[f];
                }
                backup.push(f)
            }
            return backup.join('.');
        },

        onCookieTypeChange(ctype) {
            if (this.acceptedCookieTypes.find(ct => ct.id === ctype.id)) {
                if (!ctype.required) {
                    this.acceptedCookieTypes = this.acceptedCookieTypes.filter(ct => ct.id !== ctype.id);
                }
            } else {
                this.acceptedCookieTypes = this.acceptedCookieTypes.concat(ctype);
            }
        },

        onSettingsEnter(el) {
            el.style.height = el.scrollHeight + 'px';
        },

        onSettingsLeave(el) {
            el.style.height = '';
        },

    },
});

function ctsort(a, b) { return a.id > b.id ? 1 : (a.id < b.id ? -1 : 0); }

function ctgetid(ct) { return ct.id; }

</script>

<template>
    <div
        :id="HTML_ID"
        class="ccnt" :class="{'is--settingsVisible': settingsVisible}">
        <div class="ccnt-backdrop" @click="settingsVisible = false"></div>
        <transition name="ccnt">
            <div class="ccnt-inner" v-if="isVisible">
                <div class="ccnt-container" role="alert" aria-live="polite" tabindex="0" :aria-label="str('label', 'consent_title')">
                    <section class="ccnt-banner">
                        <div class="ccnt-bannerBody wysiwyg-content" v-html="str('text', 'intro')"></div>
                        <footer>
                            <button
                                class="ccnt-button button is--transparent for--toggleSettings focus--custom"
                                :class="{'is--alt': settingsVisible}"
                                @click="settingsVisible = !settingsVisible"
                            >
                                <span>{{ str('label', 'settings') }}</span>
                                <span class="icon icon-chevron-right" aria-hidden="true"></span>
                            </button>
                            <button class="ccnt-button button is--transparent for--acceptRequired focus--custom" @click="acceptOnlyRequiredCookies()">
                                <span>{{ str('action', 'accept_required') }}</span>
                            </button>
                            <button class="ccnt-button button is--solid for--acceptAll focus--custom" @click="acceptAllCookies()">
                                <span>{{ str('action', 'accept_all') }}</span>
                            </button>
                        </footer>
                    </section>

                    <transition
                        name="settings"
                        @enter="onSettingsEnter"
                        @leave="onSettingsLeave"
                    >
                        <section class="ccnt-settings" v-if="settingsVisible">
                            <div class="ccnt-settingsBody" ref="settingsBody">
                                <div class="ccnt-settingsInner">
                                    <div class="wysiwyg-content" v-if="str('text', 'before_list')" v-html="str('text', 'before_list')"></div>
                                    <ul class="ccnt-settingsList">
                                        <li
                                            v-for="ctype of cookie_types"
                                            :key="ctype.id"
                                            :class="{'is--required': ctype.required}"
                                        >
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    class="ccnt-fieldInput"
                                                    :checked="ctype.required || !!acceptedCookieTypes.find(ct => ct.id === ctype.id)"
                                                    :disabled="ctype.required"
                                                    @change="onCookieTypeChange(ctype)"
                                                />
                                                <span class="ccnt-fieldLabel">{{ ctype.label }}</span>
                                            </label>
                                            <div class="wysiwyg-content" v-html="ctype.description"></div>
                                        </li>
                                    </ul>
                                    <div class="wysiwyg-content" v-if="str('text', 'after_list')" v-html="str('text', 'after_list')"></div>
                                </div>
                            </div>
                            <footer>
                                <button class="ccnt-button button is--solid for--saveSettings focus--custom" @click="saveSettings">
                                    <span>{{ str('action', 'save_settings') }}</span>
                                </button>
                            </footer>
                        </section>
                    </transition>
                </div>
            </div>
        </transition>
    </div>
</template>
